/**
 * <blog-listing> — category filter and "load more" for sections/blog-listing.liquid.
 *
 * Every article card is server-rendered; this element only decides which ones show.
 *   - a [data-bl-filter] chip keeps the cards whose data-category matches ("all" keeps every card),
 *     swaps in that category's [data-bl-featured] article at the top and starts again from one page
 *   - [data-bl-more] reveals one more page (page size from data-page-size-desktop / -mobile,
 *     switching at 750px) and moves focus to the first new card
 *   - the newsletter band [data-bl-band] follows the Nth visible card (data-band-after)
 *   - the progress bar and text count the visible cards of the current filter
 *
 * Label templates come from data-label: "[count]" on the button, "[shown]" / "[total]" on the text.
 *
 * Chips: the active chip's dark fill is one element ([data-bl-chips] > .bl-chips__indicator) that
 * slides to the chip just picked, crossing every chip in between; it is re-placed without motion
 * whenever the chip row reflows.
 *
 * Motion (same language as the shop-by-need tabs): on a chip the current top article and cards fade
 * out (`is-leaving`, 200ms), the new set is swapped in and drifts up with a small stagger
 * (`bl-reveal`); "load more" reveals only its new cards that way. Reduced motion → instant swap.
 */
const LEAVE_MS = 200;
const REVEAL_MS = 650;
const STAGGER_MS = 40;
const MAX_STAGGER = 8;

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class BlogListing extends HTMLElement {
  /** @type {MediaQueryList} */
  #mobile = window.matchMedia('(max-width: 749px)');
  #filter = 'all';
  #shown = 0;
  /** @type {MediaQueryList} */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** Incremented on every switch so a stale step never undoes a newer one. */
  #switchToken = 0;
  /** @type {ResizeObserver | null} */
  #chipsObserver = null;

  connectedCallback() {
    /** @type {HTMLElement[]} */
    this.cards = [...this.querySelectorAll('[data-bl-card]')];
    if (this.cards.length === 0) return;

    this.band = this.querySelector('[data-bl-band]');
    /** One featured block per chip; the active one also drops its card from the grid. */
    this.featured = [...this.querySelectorAll('[data-bl-featured]')];
    this.heading = this.querySelector('[data-bl-heading]');
    this.moreWrap = this.querySelector('[data-bl-more-wrap]');
    this.moreButton = this.querySelector('[data-bl-more]');
    this.progress = this.querySelector('[data-bl-progress]');
    this.progressText = this.progress?.querySelector('[data-label]');

    // From here on [hidden] drives visibility, not the pre-script CSS
    for (const card of this.cards) {
      card.removeAttribute('data-over-mobile');
      card.removeAttribute('data-over-desktop');
    }

    this.chips = this.querySelector('[data-bl-chips]');
    this.indicator = this.chips?.querySelector('.bl-chips__indicator');
    this.overlay = this.chips ? this.#buildOverlay(this.chips) : null;
    if (this.chips && this.indicator) {
      this.#placeIndicator(false);
      // Re-place when the row reflows (resize, wrapping) and once the web fonts have set the widths
      this.#chipsObserver = new ResizeObserver(() => this.#placeIndicator(false));
      this.#chipsObserver.observe(this.chips);
      document.fonts?.ready.then(() => this.#placeIndicator(false));
    }

    this.#shown = this.#pageSize;
    this.addEventListener('click', this.#onClick);
    this.#mobile.addEventListener('change', this.#onBreakpoint);
    this.#render();
  }

  disconnectedCallback() {
    this.#chipsObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.#mobile.removeEventListener('change', this.#onBreakpoint);
  }

  get #pageSize() {
    const value = this.#mobile.matches ? this.dataset.pageSizeMobile : this.dataset.pageSizeDesktop;
    return Number(value) || 9;
  }

  /** The featured block for the current filter, falling back to the "all" one. */
  get #activeFeatured() {
    return (
      this.featured.find((block) => block.dataset.blFeatured === this.#filter) ??
      this.featured.find((block) => block.dataset.blFeatured === 'all') ??
      null
    );
  }

  #matchingFor(featuredId) {
    const cards =
      this.#filter === 'all' ? this.cards : this.cards.filter((card) => card.dataset.category === this.#filter);
    return cards.filter((card) => card.dataset.articleId !== featuredId);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const chip = event.target.closest('[data-bl-filter]');
    if (chip instanceof HTMLElement) {
      this.#filter = chip.dataset.blFilter || 'all';
      for (const other of this.querySelectorAll('[data-bl-filter]')) {
        other.setAttribute('aria-pressed', String(other === chip));
      }
      this.#placeIndicator(true);
      this.#shown = this.#pageSize;
      this.#switch();
      return;
    }

    if (event.target.closest('[data-bl-more]')) {
      const before = new Set(this.cards.filter((card) => !card.hidden));
      const firstNew = this.#matchingFor(this.#activeFeatured?.dataset.articleId)[this.#shown];
      this.#shown += this.#pageSize;
      this.#render();
      this.#reveal(this.cards.filter((card) => !card.hidden && !before.has(card)));
      firstNew?.querySelector('h3 a')?.focus({ preventScroll: true });
    }
  };

  /**
   * Put the sliding fill under the pressed chip. Without `animate` it jumps there (first paint,
   * reflow): transitions are off while the new position is applied and flushed, then back on.
   * @param {boolean} animate
   */
  #placeIndicator(animate) {
    const chips = this.chips;
    const indicator = this.indicator;
    const chip = chips?.querySelector('[aria-pressed="true"]');
    if (!(chips instanceof HTMLElement) || !(indicator instanceof HTMLElement) || !(chip instanceof HTMLElement)) return;

    if (!animate) chips.classList.remove('bl-chips--ready');
    const x = chip.offsetLeft;
    const y = chip.offsetTop;
    const width = chip.offsetWidth;
    const height = chip.offsetHeight;
    indicator.style.setProperty('--indicator-x', `${x}px`);
    indicator.style.setProperty('--indicator-y', `${y}px`);
    indicator.style.setProperty('--indicator-w', `${width}px`);
    indicator.style.setProperty('--indicator-h', `${height}px`);

    // Same rectangle as the fill, as clip insets, so the white labels travel with it
    if (this.overlay instanceof HTMLElement) {
      this.overlay.style.setProperty('--clip-t', `${y}px`);
      this.overlay.style.setProperty('--clip-l', `${x}px`);
      this.overlay.style.setProperty('--clip-r', `${chips.clientWidth - x - width}px`);
      this.overlay.style.setProperty('--clip-b', `${chips.clientHeight - y - height}px`);
    }
    chips.classList.add('bl-chips--animated');

    if (!animate) {
      void indicator.offsetWidth; // commit the jump before transitions come back
      chips.classList.add('bl-chips--ready');
    }
  }

  /**
   * A decorative copy of the chip labels in their active colours, laid out exactly like the real
   * row and clipped to the moving fill (see `.bl-chips__overlay`).
   * @param {HTMLElement} chips
   */
  #buildOverlay(chips) {
    chips.querySelector('.bl-chips__overlay')?.remove();
    const overlay = document.createElement('span');
    overlay.className = 'bl-chips__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    for (const chip of chips.querySelectorAll('button.bl-chip')) {
      const copy = document.createElement('span');
      copy.className = 'bl-chip bl-chip--overlay';
      copy.textContent = chip.textContent;
      overlay.append(copy);
    }
    chips.append(overlay);
    return overlay;
  }

  /** Fade the current set out, swap in the new one, let it drift in. */
  async #switch() {
    const token = ++this.#switchToken;
    const animate = !this.#reducedMotion.matches;

    if (animate) {
      this.classList.add('is-leaving');
      await wait(LEAVE_MS);
      if (token !== this.#switchToken) return;
    }

    this.#render();
    this.classList.remove('is-leaving');

    if (!animate) return;
    const featuredInner = this.#activeFeatured?.querySelector('.bl-featured__inner');
    const cards = this.cards.filter((card) => !card.hidden);
    this.#reveal(featuredInner ? [featuredInner, ...cards] : cards);
  }

  /**
   * Restart the drift-in on these elements, staggered in order.
   * @param {Element[]} elements
   */
  async #reveal(elements) {
    if (this.#reducedMotion.matches || elements.length === 0) return;
    const token = this.#switchToken;

    elements.forEach((element, index) => {
      if (!(element instanceof HTMLElement)) return;
      element.classList.remove('bl-reveal');
      void element.offsetWidth; // restart the keyframes when the same element comes back
      element.style.setProperty('--reveal-index', String(Math.min(index, MAX_STAGGER)));
      element.classList.add('bl-reveal');
    });

    await wait(Math.min(elements.length, MAX_STAGGER) * STAGGER_MS + REVEAL_MS + 50);
    if (token !== this.#switchToken) return;
    for (const element of elements) element.classList.remove('bl-reveal');
  }

  /** Crossing into a bigger page size fills the page up; never hides what was already revealed. */
  #onBreakpoint = () => {
    this.#shown = Math.max(this.#shown, this.#pageSize);
    this.#render();
  };

  #render() {
    const active = this.#activeFeatured;
    for (const block of this.featured) {
      block.hidden = block !== active;
    }

    const matching = this.#matchingFor(active?.dataset.articleId);
    const shown = Math.min(this.#shown, matching.length);
    const visible = new Set(matching.slice(0, shown));

    for (const card of this.cards) {
      card.hidden = !visible.has(card);
    }

    // A category can hold a single article, which then sits on top with nothing left below
    const isEmpty = matching.length === 0;
    if (this.heading instanceof HTMLElement) this.heading.hidden = isEmpty;
    if (this.moreWrap instanceof HTMLElement) this.moreWrap.hidden = isEmpty;

    if (this.band) {
      const after = Number(this.dataset.bandAfter) || 3;
      const anchor = matching[Math.min(after, shown) - 1];
      if (anchor) {
        if (anchor.nextElementSibling !== this.band) anchor.after(this.band);
      } else {
        this.band.parentElement?.append(this.band);
      }
      this.band.hidden = false;
    }

    const remaining = matching.length - shown;
    if (this.moreButton instanceof HTMLElement) {
      this.moreButton.hidden = remaining <= 0;
      const label = this.moreButton.dataset.label;
      if (label) {
        this.moreButton.textContent = label.replace('[count]', String(Math.min(remaining, this.#pageSize)));
      }
    }

    if (this.progress instanceof HTMLElement) {
      const percent = matching.length > 0 ? (shown / matching.length) * 100 : 100;
      this.progress.style.setProperty('--bl-progress', `${percent}%`);
    }
    if (this.progressText instanceof HTMLElement && this.progressText.dataset.label) {
      this.progressText.textContent = this.progressText.dataset.label
        .replace('[shown]', String(shown))
        .replace('[total]', String(matching.length));
    }
  }
}

if (!customElements.get('blog-listing')) {
  customElements.define('blog-listing', BlogListing);
}
